import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Camera, Megaphone, Monitor, Compass, Sparkles } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSiteContent } from "../context/SiteContentContext";
import { ROUTES, servicePath } from "@/lib/routes";
import PageHero from "../components/PageHero";
import LandscapeCta from "../components/LandscapeCta";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 },
  }),
};

const iconMap = {
  camera: Camera,
  megaphone: Megaphone,
  monitor: Monitor,
  compass: Compass,
};

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const { getMergedServicePage } = useSiteContent();
  const data = getMergedServicePage(lang, slug);

  if (!data) return <Navigate to={ROUTES.services} replace />;

  const Icon = iconMap[data.icon] || Sparkles;
  const heroIsDark = slug === "ads" || slug === "consultanta";

  return (
    <main className="bg-white" data-testid={`service-page-${slug}`}>
      <PageHero
        variant={heroIsDark ? "dark" : "light"}
        eyebrow={data.eyebrow}
        title={data.title}
        description={data.lead}
        badgeIcon={Icon}
        testId={`service-hero-${slug}`}
      />

      {/* Intro */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-stretch gap-10 px-6 sm:px-12 lg:grid-cols-2 lg:gap-14 lg:px-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="order-2 flex flex-col justify-center lg:order-1"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#3e2723]/45">01 · Prezentare</p>
            <h2 className="mt-4 font-display text-[1.75rem] font-black leading-[1.08] tracking-tighter text-[#3e2723] sm:text-4xl lg:text-[2.35rem]">
              {data.intro_title}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#7a5c55] sm:text-lg">{data.intro_body}</p>
            <Link
              to={ROUTES.contact}
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#3e2723] px-6 py-3.5 text-sm font-semibold text-[#fbd9e5] transition hover:-translate-y-0.5 hover:bg-[#2a1a17] sm:w-auto"
              data-testid={`service-${slug}-intro-cta`}
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="order-1 flex min-h-[300px] lg:order-2 lg:min-h-0"
          >
            <div className="relative h-full min-h-[300px] w-full overflow-hidden rounded-[1.75rem] shadow-[0_24px_60px_-24px_rgba(62,39,35,0.3)] sm:rounded-[2rem]">
              <img src={data.intro_image} alt={data.intro_image_alt?.trim() || data.title} className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#3e2723]/20 via-transparent to-[#fbd9e5]/15" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features / Livrabile */}
      <section className="bg-[#faf8f8] py-16 sm:py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            className="max-w-2xl"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#3e2723]/45">02 · Livrabile</p>
            <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#3e2723] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
              {data.features_title}
            </h2>
          </motion.div>

          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3 lg:gap-4">
            {data.features.map((f, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="group flex gap-4 rounded-[1.5rem] border border-[#3e2723]/8 bg-white p-5 transition duration-500 hover:border-[#3e2723]/15 hover:shadow-[0_12px_40px_rgba(62,39,35,0.07)] sm:rounded-[1.75rem] sm:p-6"
              >
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#fbd9e5] text-[#3e2723] transition group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5]">
                  <Check className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold tracking-tight text-[#3e2723] sm:text-lg">{f.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-[#7a5c55]">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <LandscapeCta
        eyebrow={data.eyebrow}
        title={data.cta_title}
        subtitle={data.cta_subtitle}
        buttonLabel={t.nav.cta}
        testId={`service-${slug}-cta-section`}
        buttonTestId={`service-${slug}-cta`}
      />
    </main>
  );
}
